import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import * as Tone from 'tone'

import { getDrumsInnerRoute, getInstrumentsInnerRoute, getPlaystylesInnerRoute } from '../../../api/constants'
import { getPreviewByIdKeyOrHashInnerRoute } from '../../../api/previews'
import { getInstrumentKey, setupLayers } from '../../../utils/instrumentsUtils'
import {
  getFirstNonEmptyPart,
  getPartIdByChordId,
  getPartLengthInBars,
  getPartOffsetInBars,
} from '../../../utils/progUtils'
import { copyObj } from '../../../utils/stringUtils'
import { InstrumentLayer, Preview, Prog, ProgPart } from '../../../utils/types'
import PartsSimpleMenu from '../../editor/PartsMenu/PartsSimpleMenu'
import Player from '../../editor/hooks/player/Player'
import IOSAudioContextFix from '../IOSAudioContextFix/IOSAudioContextFix'
import PreviewBox from './Preview/Preview'

type Props = {
  handleLoaded?: () => void
  fullPage?: boolean
  renderFooter?: (a: () => ReactElement, b: () => ReactElement) => ReactElement
  renderHeader?: (a: () => ReactElement) => ReactElement
  renderBitsPreview?: (a: Player, b: Prog | undefined, c: number, scale: number) => ReactElement
}

const PreviewPlayer: React.FC<Props> = ({ handleLoaded, fullPage, renderFooter, renderBitsPreview, renderHeader }) => {
  const iOSAudioContextFixRef = useRef<HTMLAudioElement>(null)
  const playheadPositionInterval = useRef<any>(null)

  const [playheadPosition, setPlayheadPosition] = useState<number | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isRepeat, setIsRepeat] = useState(true)
  const [volume, setVolume] = useState(100)
  const [activeChordId, setActiveChordId] = useState<number | null>(null)
  const [toneStarted, setToneStarted] = useState(false)
  const [currentLoaded, setCurrentLoaded] = useState<string | null>(null)
  const [currentPartId, setCurrentPartId] = useState(1)
  const [progLoading, setProgLoading] = useState<string | null>(null)
  const [progs, setProgs] = useState<{ id: string; data: Preview; el: Element }[]>([])

  const [chordInstrumentsData, setChordInstrumentsData] = useState<any>(null)
  const [drumInstrumentsData, setDrumInstrumentsData] = useState<any>(null)
  const [playstylesData, setPlaystylesData] = useState<any>(null)

  useEffect(() => {
    getInstrumentsInnerRoute().then((res) => setChordInstrumentsData(res))
    getDrumsInnerRoute().then((res) => setDrumInstrumentsData(res))
    getPlaystylesInnerRoute().then((res) => setPlaystylesData(res))
  }, [])

  const resetCurrentPart = useCallback(() => {
    const partId = getFirstNonEmptyPart((progs.find((p) => p.id === currentLoaded) || progs[0])?.data.prog as Prog)
      ?.id as number
    partId && setCurrentPartId(partId)
  }, [progs, currentLoaded])

  const player: Player = useMemo(
    () =>
      new Player(
        setIsPlaying,
        (id) => {
          if (id === -1) {
            resetCurrentPart()
          } else {
            const partId = getPartIdByChordId(id)
            partId && setCurrentPartId(partId)
            setActiveChordId(id)
          }
        },
        (partId) => {
          if (partId) setCurrentPartId(partId)
        },
        (partId) => {
          if (partId) setCurrentPartId(partId)
        },
      ),
    [],
  )
  const requestPosition = (player: Player, prog: Prog, currentPartId: number, looped?: boolean) => {
    const partLength = getPartLengthInBars(prog, currentPartId)
    const barsOffset = getPartOffsetInBars(prog, currentPartId)
    const position = player.getTransportPosition(looped ? 0 : barsOffset)

    return 1 + (position % partLength)
  }

  useEffect(() => {
    clearInterval(playheadPositionInterval.current)
    playheadPositionInterval.current = null

    if (isPlaying) {
      playheadPositionInterval.current = setInterval(
        () => setPlayheadPosition(requestPosition(player, progs[0].data.prog, currentPartId, isRepeat)),
        20,
      )
    } else {
      setPlayheadPosition(null)
    }
  }, [isPlaying, playheadPositionInterval.current, player, progs, currentPartId, isRepeat])
  useEffect(() => {
    resetCurrentPart()
  }, [progs, currentLoaded])
  useEffect(() => {
    if (!chordInstrumentsData || !drumInstrumentsData || !playstylesData) return
    setTimeout(() => {
      ;(async () => {
        const playBoxes = Array.from(document.querySelectorAll('._player_'))
        const previewIds = playBoxes.map((b) => b.getAttribute('data-preview-id'))
        const previews = await Promise.all(
          previewIds.map((id) => {
            try {
              return getPreviewByIdKeyOrHashInnerRoute(String(id))
            } catch (e) {
              return null
            }
          }),
        )

        const previewsMapped = previews
          .map((preview, index) => ({
            data: getProgLayers(preview as Preview).preview,
            id: preview?.id || '',
            el: playBoxes[index],
          }))
          .filter((preview) => !!preview.data)

        setProgs(previewsMapped)
        setCurrentPartId(previewsMapped[0]?.data.prog.parts[0].id)
        handleLoaded && handleLoaded()

        if (previewsMapped.length) {
          if (previewsMapped[0].data?.prog.volume) {
            setVolume(previewsMapped[0].data.prog.volume)
          }
        }
      })()
    }, 1000)

    return () => player.reset()
  }, [chordInstrumentsData, drumInstrumentsData, playstylesData])
  useEffect(() => {
    if (!fullPage) return

    const onKeyDown = (e: KeyboardEvent) => {
      // need this to stop keys actions when modals are opened
      // @ts-ignore
      if (e.path?.map((a) => a.className).some((className) => className?.includes('center-modal'))) {
        return
      }
      if (e.key === ' ') {
        handlePlay(isPlaying, progs[0])
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [progs, isPlaying, toneStarted])

  const getProgLayers = (preview: Preview) => {
    if (!chordInstrumentsData || !drumInstrumentsData || !playstylesData) {
      return { progChordLayers: [], progMelodyLayers: [], progDrumLayers: [], preview }
    }

    const newPreview = copyObj(preview) as Preview

    const progChordLayers: InstrumentLayer[] = []
    const progMelodyLayers: InstrumentLayer[] = []
    const progDrumLayers: InstrumentLayer[] = []

    const setupPartLayers = (
      part: ProgPart,
      partIndex: number,
      layersKey: any,
      instrumentsData: any,
      playstylesData: any,
      layers: InstrumentLayer[],
    ) => {
      // @ts-ignore
      part[layersKey]?.forEach((layer, index) => {
        const key = getInstrumentKey(layer.instrument, index, part.id)
        const setupedLayer = copyObj(setupLayers([layer], instrumentsData, playstylesData)[0])

        // @ts-ignore
        newPreview.prog.parts[partIndex][layersKey][index] = copyObj(setupedLayer)

        setupedLayer.instrument.key = key

        layers.push(setupedLayer)
      })
    }

    preview.prog.parts.forEach((part, p_i) => {
      setupPartLayers(part, p_i, 'chordLayers', chordInstrumentsData, playstylesData, progChordLayers)
      setupPartLayers(part, p_i, 'melodyLayers', chordInstrumentsData, undefined, progMelodyLayers)
      setupPartLayers(part, p_i, 'drumLayers', drumInstrumentsData, undefined, progDrumLayers)
    })

    return { progChordLayers, progMelodyLayers, progDrumLayers, preview: newPreview }
  }
  const playProg = async (data: Preview, id: string) => {
    setCurrentLoaded(id)
    setVolume(data.prog.volume)
    player.setBpm(data.prog.bpm)
    player.repeat(isRepeat)
    player.drumsPlayer?.setupProgPlayback(data.prog)
    player.setupProgPlayback(data.prog)
    setProgLoading(null)
  }
  const handlePlay = async (playing: boolean, p: { id: string; data: Preview; el: Element }) => {
    if (!toneStarted) {
      await handleSetupPreview()
      return
    }

    if (playing) {
      player.pause()
      return
    }
    if (isPlaying) {
      setActiveChordId(null)
      player.reset()
    }
    if (currentLoaded !== p.id) {
      setProgLoading(p.id)
      setTimeout(async () => {
        await playProg(p.data, p.id)
        setIsPlaying(true)
        player.play()
      }, 500)
    } else {
      player.play()
    }
  }
  const handleSetupPreview = async (e?: MouseEvent, previewsRaw?: any[]) => {
    if (iOSAudioContextFixRef.current) {
      try {
        iOSAudioContextFixRef.current.play()
      } catch (e) {
        console.error(e)
      }
    }

    const previews = previewsRaw || progs

    const dataPlay =
      // @ts-ignore
      e?.target?.getAttribute('data-play') || e?.target?.closest('[data-play]')?.getAttribute('data-play')
    const dataPlayFullPage = fullPage ? previews[0].id : undefined

    const prog = previews.find((p) => p.id === (dataPlay || dataPlayFullPage))
    if (prog) setProgLoading(prog.id)

    const progData = prog || previews[0]
    const data = progData.data
    player.init(data.prog.volume, data.prog)

    await Tone.start()

    setTimeout(async () => {
      const { progChordLayers, progMelodyLayers, progDrumLayers } = getProgLayers(previews[0].data)

      await Promise.all([
        player.setupChordSynth(previews[0].data.prog, progChordLayers, {}, chordInstrumentsData),
        player.setupMelodySynth(previews[0].data.prog, progMelodyLayers, chordInstrumentsData),
        player.setupDrumsSynth(previews[0].data.prog, progDrumLayers),
      ])

      // if play was clicked and tone was not initiated yet - trigger play click manually
      if (prog) {
        setProgLoading(prog.id)

        await playProg(progData.data, progData.id)

        setIsPlaying(true)
        player.play()
      } else {
        await playProg(progData.data, progData.id)
      }

      setToneStarted(true)
    }, 200)
  }

  return (
    <div>
      <IOSAudioContextFix audioRef={iOSAudioContextFixRef} />
      {progs.map((p) => {
        const playing = isPlaying && currentLoaded === p.id
        return createPortal(
          <>
            {fullPage &&
              renderHeader &&
              renderHeader(() => (
                <PartsSimpleMenu
                  player={player}
                  parts={p.data?.prog?.parts}
                  currentPartId={currentPartId}
                  prog={p.data.prog}
                  handlePartClick={(part) => {
                    if (!toneStarted) return
                    setCurrentPartId(part.id)
                    player?.setTime(getPartOffsetInBars(p.data?.prog, part.id))
                  }}
                />
              ))}
            <PreviewBox
              prog={p.data?.prog}
              bpm={p.data?.prog.bpm}
              playheadPosition={playheadPosition}
              fullPage={fullPage}
              id={p.id}
              currentPartId={currentPartId}
              previewKey={p.data.key || p.data.id}
              activeChordId={playing || currentLoaded === p.id ? activeChordId : undefined}
              playDisabled={false}
              loading={progLoading === p.id}
              isPlaying={playing}
              playChord={
                !toneStarted
                  ? undefined
                  : async (ch) => {
                      if (currentLoaded !== p.id) {
                        if (isPlaying) {
                          player.reset()
                        }
                        await playProg(p.data, p.id)
                      }
                      player.playChord(p.data?.prog, ch.id)
                      setActiveChordId(ch.id)
                      setCurrentLoaded(p.id)
                    }
              }
              renderFooter={renderFooter}
              repeat={isRepeat}
              handleRepeat={() =>
                setIsRepeat((v) => {
                  player.repeat(!v)

                  return !v
                })
              }
              handleReset={async () => {
                if (!playing && currentLoaded !== p.id) {
                  return
                }
                resetCurrentPart()
                setActiveChordId(null)
                player.reset()
              }}
              handlePlay={() => handlePlay(playing, p)}
              volume={volume}
              handleVolumeChange={(v) => {
                setVolume(v)
                player.setVolume(v)
              }}
              renderDrumsPreview={(scale: number) =>
                renderBitsPreview ? renderBitsPreview(player, progs[0]?.data.prog, currentPartId, scale) : <></>
              }
            />
          </>,
          p.el,
        )
      })}
    </div>
  )
}

export default PreviewPlayer
